import type { BiometricDataDTO } from '@/modules/employees/infrastructure/dto/EmployeeDTO';
import { isCardBiometricData } from '@/modules/employees/infrastructure/dto/EmployeeDTO';

/** Result returned by the duplicate-card pre-check. */
export interface DuplicateCardCheckResult {
  conflict: boolean;
  matchingDecimal?: string;
}

/**
 * Pure use case: checks whether a scanned card decimal already exists
 * in the employee's biometric_data[] before hitting the remote API.
 *
 * Filtering rules:
 * - Entries where id_template_biometric === 14 (face/photo) are IGNORED.
 * - Entries where id_template_biometric === 5  (PIN)         are IGNORED.
 * - All other entries are card records and are checked.
 *
 * Match logic (EITHER → conflict):
 * - parseInt(entry.decimal, 10) === decimal
 * - entry.decimal_short === decimal
 */
export function checkDuplicateCardEnrollment(
  biometricData: BiometricDataDTO[],
  decimal: number,
): DuplicateCardCheckResult {
  for (const entry of biometricData) {
    // Skip face (14) and PIN (5) entries — only card entries remain
    if (entry.id_template_biometric === 14 || entry.id_template_biometric === 5) {
      continue;
    }

    if (!isCardBiometricData(entry.biometric_data)) {
      continue;
    }

    const card = entry.biometric_data;
    const decimalMatch      = parseInt(card.decimal, 10) === decimal;
    const decimalShortMatch = card.decimal_short === decimal;

    if (decimalMatch || decimalShortMatch) {
      return { conflict: true, matchingDecimal: card.decimal };
    }
  }

  return { conflict: false };
}
